import { Fragment, useState, type RefObject } from "react";
import { MessageBubble } from "./MessageBubble";
import { Composer } from "./Composer";
import type { LoadImage } from "./LazyImage";
import { dayKey, dateLabel } from "../format";
import { addressedToHumanIds, groupLabel, opensSpeakerRun, HUMAN_PARTICIPANT_ID, type ChatMessage, type GroupChannel } from "../protocol";

/** Who — other than the observer — has read this message, from its per-participant receipts. A group has
 *  several readers, so a single tick can't say who; empty when nobody has read it yet. */
function readers(m: ChatMessage, resolveSender: (sessionId: string) => string): string[] {
  return Object.entries(m.receipts ?? {})
    .filter(([id, state]) => id !== HUMAN_PARTICIPANT_ID && (state === "read" || state === "ack"))
    .map(([id]) => resolveSender(id))
    .filter((n) => n.trim());
}

/** A #Group-chat thread: agent-to-agent DMs with the human observing. Unlike a 1:1 chat, every agent message
 *  is labelled with WHO sent it (resolved from the participant roster), since several agents share the thread. */
export function GroupThread({
  group,
  messages,
  resolveSender,
  endRef,
  scrollRef,
  onScroll,
  load,
  onOpen,
  onSend,
  onBack,
  onClose,
  unreadFromId,
}: {
  group: GroupChannel;
  messages: ChatMessage[];
  /** sessionId → display label (from the live session list, falling back to the stored roster label). */
  resolveSender: (sessionId: string) => string;
  endRef: RefObject<HTMLDivElement>;
  scrollRef?: RefObject<HTMLDivElement>;
  onScroll?: () => void;
  load: LoadImage;
  onOpen: (url: string) => void;
  onSend: (text: string) => void;
  onBack: () => void;
  onClose: () => void;
  /** The message the thread opens on — gets the "Unread" rule above it, as a 1:1 chat does. Null when
   *  nothing is unread. */
  unreadFromId?: string | null;
}) {
  const [draft, setDraft] = useState("");
  const noop = () => {};
  const addressed = addressedToHumanIds(messages);
  return (
    <>
      <header className="idea-head group-head">
        <button className="back" onClick={onBack} aria-label="Back">
          ‹
        </button>
        <div className="idea-title group-title-wrap">
          <span className="group-title-name">{groupLabel(group, (p) => resolveSender(p.id) || p.label)}</span>
          <div className="group-participants">
            {group.participants.map((p) => (
              <span key={p.id} className={`participant-chip ${p.kind}`} title={p.repo ?? p.kind}>
                {resolveSender(p.id) || p.label || (p.kind === "human" ? "You" : "Agent")}
              </span>
            ))}
          </div>
        </div>
        <button
          className="group-close-btn"
          title="Close group"
          onClick={() => {
            if (confirm(`Close “${groupLabel(group, (p) => resolveSender(p.id) || p.label)}”? This closes it for everyone in it.`)) {
              onClose();
            }
          }}
        >
          Close
        </button>
      </header>
      <div className="messages" ref={scrollRef} onScroll={onScroll}>
        {messages.map((m, i) => {
          const prev = messages[i - 1];
          const showSender = opensSpeakerRun(m, prev);
          const addressee = m.to ? resolveSender(m.to) : "";
          return (
            <Fragment key={m.id}>
              {(i === 0 || dayKey(m.ts) !== dayKey(messages[i - 1].ts)) && (
                <div className="date-sep">
                  <span>{dateLabel(m.ts)}</span>
                </div>
              )}
              {/* Above the first unread and left there: opening the group stamps its receipts, so a rule
                  recomputed from live state would disappear as it was being looked at. */}
              {m.id === unreadFromId && (
                <div className="unread-sep" data-unread-anchor>
                  <span>Unread</span>
                </div>
              )}
              {showSender && (
                <div className="group-sender">
                  {addressee ? `${resolveSender(m.sender!)} → ${addressee}` : resolveSender(m.sender!)}
                </div>
              )}
              <MessageBubble
                m={m}
                load={load}
                onOpen={onOpen}
                onOpenDoc={noop}
                onOption={noop}
                quoted={m.replyTo ? (messages.find((x) => x.id === m.replyTo)?.text ?? "") : undefined}
                addressed={addressed.has(m.id)}
              />
              {m.role === "user" && readers(m, resolveSender).length > 0 && (
                <div className="group-readby">Read by {readers(m, resolveSender).join(", ")}</div>
              )}
            </Fragment>
          );
        })}
        <div ref={endRef} />
      </div>
      <Composer
        draft={draft}
        setDraft={setDraft}
        onSend={() => {
          const t = draft.trim();
          if (!t) return;
          onSend(t);
          setDraft("");
        }}
        placeholder="Message the group…"
      />
    </>
  );
}
